import { LogIn, ShieldCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './auth-context'

export function LoginPage() {
  const { state, user, signIn, error: sessionError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState(sessionError)
  const [loading, setLoading] = useState(false)

  if (state === 'authenticated' && user) return <Navigate replace to={homeForRole(user.profile.role)} />

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (loading) return
    setLoading(true)
    setMessage('')
    try {
      const authenticated = await signIn(email, password)
      const requestedDestination = typeof location.state === 'object' && location.state && 'from' in location.state
        ? String(location.state.from)
        : null
      const destination = destinationForRole(authenticated.profile.role, requestedDestination)
      navigate(destination, { replace: true })
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : 'Não foi possível autenticar.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="brand-mark"><ShieldCheck aria-hidden="true" /></div>
        <p className="eyebrow">Sistema de Requisições</p>
        <h1 id="login-title">Acesso seguro</h1>
        <p className="supporting-copy">Entre com seu e-mail e senha para acessar sua área de trabalho.</p>
        <form onSubmit={submit} className="login-form">
          <label>
            <span>E-mail</span>
            <input required type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <label>
            <span>Senha</span>
            <input required type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} />
          </label>
          <button type="submit" disabled={loading}>
            <LogIn size={18} aria-hidden="true" />
            {loading ? 'Validando...' : 'Entrar'}
          </button>
        </form>
        <Link className="login-back" to="/esqueci-senha">Esqueci minha senha</Link>
        {message ? <p className="feedback feedback-error" role="alert">{message}</p> : null}
        <p className="gate-label">Requisição de Materiais · Acesso por perfil</p>
      </section>
    </main>
  )
}

function homeForRole(role: 'REQUISITANTE' | 'COMPRADOR' | 'ADMIN'): string {
  if (role === 'REQUISITANTE') return '/app/requisicoes'
  if (role === 'COMPRADOR') return '/app/compras'
  return '/app/admin'
}

function destinationForRole(role: 'REQUISITANTE' | 'COMPRADOR' | 'ADMIN', requested: string | null): string {
  if (!requested?.startsWith('/app/')) return homeForRole(role)
  if (role === 'REQUISITANTE' && requested.startsWith('/app/requisicoes')) return requested
  if (role === 'COMPRADOR' && requested.startsWith('/app/compras')) return requested
  if (requested === '/app/minha-conta') return requested
  if (role === 'ADMIN' && (requested.startsWith('/app/admin') || requested.startsWith('/app/compras') || requested.startsWith('/app/requisicoes'))) return requested
  return homeForRole(role)
}
